<mat-sidenav-container class="pol-sidenav-host">

  <!-- ── Main content ── -->
  <mat-sidenav-content>
    <div class="pol-page">

      <!-- ── Info banner ── -->
      <div class="org-banner" *ngIf="bannerVisible">
        <mat-icon class="org-banner-icon">info_outline</mat-icon>
        <div class="org-banner-text">
          <strong>Policy Templates</strong>
          <p>
            Policy templates define the lifecycle rules for cloud resources — including creation mode,
            daily cleanup actions, and end-of-class cleanup behaviour.
            Policies are reusable and can be applied across multiple classes and requisitions.
          </p>
        </div>
        <div class="org-banner-actions">
          <mat-checkbox [(ngModel)]="bannerDismissForever" class="banner-dismiss-check">
            Don't show again
          </mat-checkbox>
          <button mat-icon-button class="banner-close-btn"
                  (click)="dismissBanner()"
                  matTooltip="Dismiss">
            <mat-icon>close</mat-icon>
          </button>
        </div>
      </div>

      <!-- Toolbar -->
      <mat-card class="pol-toolbar-card">
        <mat-card-content class="pol-toolbar">

          <mat-form-field appearance="outline" class="search-field">
            <mat-label>Search policies</mat-label>
            <input matInput [(ngModel)]="searchTerm" (input)="onSearchChange()"
                   placeholder="Name or description…">
            <mat-icon matPrefix>search</mat-icon>
            <button *ngIf="searchTerm" matSuffix mat-icon-button
                    (click)="searchTerm=''; onSearchChange()">
              <mat-icon>close</mat-icon>
            </button>
          </mat-form-field>

          <!-- Resource type filter chips -->
          <div class="type-filters">
            <span class="filter-label">Filter:</span>
            <button
              class="type-filter-chip"
              [class.active]="vmChipActive"
              (click)="toggleVmChip()">
              <mat-icon class="chip-mat-icon">computer</mat-icon>
              VM
            </button>
            <button
              class="type-filter-chip"
              [class.active]="uaChipActive"
              (click)="toggleUaChip()">
              <mat-icon class="chip-mat-icon">manage_accounts</mat-icon>
              User Account
            </button>
          </div>

          <span class="toolbar-spacer"></span>

          <button mat-raised-button color="primary" routerLink="/policy-template/create">
            <mat-icon>add</mat-icon>
            Add Policy Template
          </button>

        </mat-card-content>
      </mat-card>

      <!-- Table card -->
      <mat-card class="pol-table-card">
        <mat-progress-bar *ngIf="loading" mode="indeterminate"></mat-progress-bar>

        <div *ngIf="error" class="pol-error">
          <mat-icon color="warn">error_outline</mat-icon>
          <span>{{ error }}</span>
          <button mat-button color="primary" (click)="loadPolicies()">
            <mat-icon>refresh</mat-icon> Retry
          </button>
        </div>

        <table mat-table [dataSource]="dataSource" matSort class="pol-table">

          <!-- Resource Type -->
          <ng-container matColumnDef="resourceType">
            <th mat-header-cell *matHeaderCellDef mat-sort-header>Resource Type</th>
            <td mat-cell *matCellDef="let p">
              <span class="type-badge"
                    [class.type-badge--vm]="p.resourceType === 'Vm'"
                    [class.type-badge--ua]="p.resourceType === 'UserAccount'">
                <mat-icon class="badge-icon">{{ getResourceTypeIcon(p.resourceType) }}</mat-icon>
                {{ p.resourceType === 'Vm' ? 'VM' : 'User Account' }}
              </span>
            </td>
          </ng-container>

          <!-- Policy Name -->
          <ng-container matColumnDef="policyName">
            <th mat-header-cell *matHeaderCellDef mat-sort-header>Policy Name</th>
            <td mat-cell *matCellDef="let p" class="pol-name">{{ p.policyName }}</td>
          </ng-container>

          <!-- Description -->
          <ng-container matColumnDef="description">
            <th mat-header-cell *matHeaderCellDef>Description</th>
            <td mat-cell *matCellDef="let p" class="pol-desc-cell"
                [matTooltip]="(p.description?.length ?? 0) > 50 ? p.description : ''"
                matTooltipShowDelay="400">
              {{ p.description || '—' }}
            </td>
          </ng-container>

          <!-- Updated At -->
          <ng-container matColumnDef="updatedAt">
            <th mat-header-cell *matHeaderCellDef mat-sort-header>Last Updated</th>
            <td mat-cell *matCellDef="let p">{{ p.updatedAt | date:'dd MMM yyyy' }}</td>
          </ng-container>

          <!-- Actions -->
          <ng-container matColumnDef="actions">
            <th mat-header-cell *matHeaderCellDef></th>
            <td mat-cell *matCellDef="let p" (click)="$event.stopPropagation()">
              <button *ngIf="canArchive" mat-icon-button matTooltip="Archive this template" (click)="archiveTemplate(p)"
                      aria-label="Archive template">
                <mat-icon>inventory_2</mat-icon>
              </button>
            </td>
          </ng-container>

          <tr mat-header-row *matHeaderRowDef="displayedColumns; sticky: true"></tr>
          <tr mat-row *matRowDef="let row; columns: displayedColumns;"
              class="pol-row"
              (click)="onRowClick(row)"></tr>

          <tr class="mat-row" *matNoDataRow>
            <td class="mat-cell no-data-cell" [attr.colspan]="displayedColumns.length">
              <div class="no-data-message">
                <mat-icon>description</mat-icon>
                <p>{{ searchTerm || hasActiveFilter ? 'No templates match the current filters' : 'No policy templates found' }}</p>
              </div>
            </td>
          </tr>

        </table>

        <mat-paginator [pageSizeOptions]="pageSizeOptions" [pageSize]="pageSize" appPersistPageSize="policyTemplates:list" showFirstLastButtons></mat-paginator>
      </mat-card>

    </div>
  </mat-sidenav-content>

  <!-- ── Right-side details drawer ── -->
  <mat-sidenav #detailsDrawer position="end" mode="over"
               [opened]="showDetailDrawer"
               (closedStart)="closeDetailDrawer()"
               class="pol-details-sidenav">

    <div class="pol-drawer-header">
      <div class="pol-drawer-title">
        <mat-icon>policy</mat-icon>
        <span>Policy Details</span>
      </div>
      <button mat-icon-button (click)="closeDetailDrawer()" matTooltip="Close">
        <mat-icon>close</mat-icon>
      </button>
    </div>

    <mat-divider></mat-divider>

    <div class="pol-drawer-body" *ngIf="loadingDetail">
      <div class="detail-loading">
        <mat-spinner diameter="32"></mat-spinner>
        <p>Loading details…</p>
      </div>
    </div>

    <div class="pol-drawer-body" *ngIf="selectedDetail && !loadingDetail">
      <div class="detail-content">

        <!-- Name + type badge -->
        <div class="detail-name-row">
          <span class="type-badge"
                [class.type-badge--vm]="selectedDetail.resourceType === 'Vm'"
                [class.type-badge--ua]="selectedDetail.resourceType === 'UserAccount'">
            <mat-icon class="badge-icon">{{ getResourceTypeIcon(selectedDetail.resourceType) }}</mat-icon>
            {{ selectedDetail.resourceType === 'Vm' ? 'VM Policy' : 'User Account Policy' }}
          </span>
          <h3 class="detail-name">{{ selectedDetail.policyName }}</h3>
        </div>

        <p class="detail-desc" *ngIf="selectedDetail.description">{{ selectedDetail.description }}</p>
        <p class="detail-desc detail-desc--empty" *ngIf="!selectedDetail.description">No description.</p>

        <mat-accordion [multi]="true" class="detail-accordion">

          <!-- VM steps -->
          <ng-container *ngIf="selectedDetail.resourceType === 'Vm' && selectedDetail.vmSteps">

            <!-- Resource Creation -->
            <mat-expansion-panel [expanded]="true">
              <mat-expansion-panel-header>
                <mat-panel-title><mat-icon>add_circle_outline</mat-icon>&nbsp;Resource Creation</mat-panel-title>
              </mat-expansion-panel-header>
              <p>{{ selectedDetail.vmSteps!.creationMode === 'on_demand' ? 'On Demand' : selectedDetail.vmSteps!.creationMode }}</p>
            </mat-expansion-panel>

            <mat-expansion-panel [expanded]="true">
              <mat-expansion-panel-header>
                <mat-panel-title><mat-icon>today</mat-icon>&nbsp;Daily Cleanup</mat-panel-title>
              </mat-expansion-panel-header>
              <p>{{ formatCleanupAction(selectedDetail.vmSteps!.dailyCleanup) }}</p>
            </mat-expansion-panel>

            <mat-expansion-panel [expanded]="true">
              <mat-expansion-panel-header>
                <mat-panel-title><mat-icon>event_available</mat-icon>&nbsp;Class End Cleanup</mat-panel-title>
              </mat-expansion-panel-header>
              <p>{{ formatCleanupAction(selectedDetail.vmSteps!.classEndCleanup) }}</p>
            </mat-expansion-panel>
          </ng-container>

          <!-- UA steps -->
          <ng-container *ngIf="selectedDetail.resourceType === 'UserAccount' && selectedDetail.uaSteps">

            <!-- Resource Creation -->
            <mat-expansion-panel [expanded]="true">
              <mat-expansion-panel-header>
                <mat-panel-title><mat-icon>add_circle_outline</mat-icon>&nbsp;Resource Creation</mat-panel-title>
              </mat-expansion-panel-header>
              <p>{{ selectedDetail.uaSteps!.creationMode === 'on_demand' ? 'On Demand' : selectedDetail.uaSteps!.creationMode }}</p>
            </mat-expansion-panel>

            <mat-expansion-panel [expanded]="true">
              <mat-expansion-panel-header>
                <mat-panel-title><mat-icon>today</mat-icon>&nbsp;Daily Cleanup</mat-panel-title>
              </mat-expansion-panel-header>
              <p>{{ formatCleanupAction(selectedDetail.uaSteps!.dailyCleanup) }}</p>
            </mat-expansion-panel>

            <mat-expansion-panel [expanded]="true">
              <mat-expansion-panel-header>
                <mat-panel-title><mat-icon>event_available</mat-icon>&nbsp;Class End Cleanup</mat-panel-title>
              </mat-expansion-panel-header>
              <p>{{ formatCleanupAction(selectedDetail.uaSteps!.classEndCleanup) }}</p>
            </mat-expansion-panel>

            <mat-expansion-panel [expanded]="true">
              <mat-expansion-panel-header>
                <mat-panel-title><mat-icon>currency_rupee</mat-icon>&nbsp;Quota Limit</mat-panel-title>
              </mat-expansion-panel-header>
              <p>{{ formatQuotaLimit(selectedDetail.uaSteps!.quotaLimit) }}</p>
            </mat-expansion-panel>
          </ng-container>

        </mat-accordion>

        <!-- Metadata -->
        <div class="detail-meta">
          <mat-divider></mat-divider>
          <p class="meta-row">
            <mat-icon class="meta-icon">schedule</mat-icon>
            Updated {{ selectedDetail.updatedAt | date:'dd MMM yyyy' }}
          </p>
        </div>
      </div>
    </div>

  </mat-sidenav>

</mat-sidenav-container>
